import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { iRootDispatch, iRootState } from '../../../../store';
import { AvailableBrokersAndEstatesState } from '../../../../store/types/temp-types';
import { FranchiseListPageProps } from '../..';
import { useDropDownUsers } from '../../../../services/broker';
import { UsersToCustomDropDownOptions } from '../../../../util';
import { unmaskCpfOrCnpj } from '../../../../services/masks';
import FranchiseListPageTable from '../Table';
import { useQuery } from '@tanstack/react-query';
import { getFranchisesList } from '../../../../services/franchises';
import { FranchiseState } from '../../../../types/FranchiseState';
import { statusOptions, typeOptions } from '../../types';
import ViewModal from '../ViewModal';
import { dangerNotification } from '../../../../services/notification';
import FilterBar, { FilterField, FilterValues } from '../../../../components/Filter';

type SearchField = 'document' | 'name' | 'address';

type ApiFilters = {
  type?: string;
  argument?: string;
  accountManager?: string;
  multiFranchiseId?: string;
  status?: string;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name', 'address'];

const initialFilterValues: FilterValues = {
  type: 'document',
  argument: '',
  status: '',
  accountManager: '',
  multiFranchiseId: ''
};

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const normalizeToApiFilter = (values: FilterValues): ApiFilters => {
  const searchType = (values.type as SearchField) || 'document';
  let argument = String(values.argument || '').trim();

  if (searchType === 'document' && argument) {
    argument = unmaskCpfOrCnpj(argument);
  }

  const filter: ApiFilters = {};

  if (argument && SEARCH_FIELDS.includes(searchType)) {
    filter.type = searchType;
    filter.argument = argument;
  }

  if (values.status) filter.status = String(values.status);
  if (values.accountManager) filter.accountManager = String(values.accountManager);
  if (values.multiFranchiseId) {
    filter.multiFranchiseId = String(values.multiFranchiseId);
  }

  return filter;
};

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

const FranchiseListPage = (props: FranchiseListPageProps) => {
  const { user, availableBrokersAndEstates } = props;
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemSelected, setItemSelected] = useState<FranchiseState | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState<boolean>(false);
  const [total, setTotal] = useState(0);

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() =>
    normalizeToApiFilter(initialFilterValues)
  );
  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const isEstateUser = Boolean(user?.role?.includes('ESTATE'));

  const { data: usersData, isLoading: isAccountManagerLoading } = useDropDownUsers(
    user?.broker?.id
  );

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    return UsersToCustomDropDownOptions(
      false,
      usersData.map((item: any) => ({
        id: item.id,
        name: item.name
      }))
    ).map((item: any) => ({
      value: String(item.value),
      text: String(item.text)
    }));
  }, [usersData]);

  const multiFranchiseOptions = useMemo(
    () =>
      (availableBrokersAndEstates?.multiFranchises || []).map(item => ({
        value: String(item.value || ''),
        text: String(item.text || '')
      })),
    [availableBrokersAndEstates?.multiFranchises]
  );

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'document',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusOptions),
        getDisplayValue: values => {
          const option = statusOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'select',
        name: 'multiFranchiseId',
        label: 'Multi Franquia',
        placeholder: 'Selecionar...',
        options: toSelectOptions(multiFranchiseOptions),
        getDisplayValue: values => {
          const option = multiFranchiseOptions.find(
            item => String(item.value) === String(values.multiFranchiseId)
          );
          return option ? option.text : undefined;
        }
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'accountManager',
        label: 'Gestor de Contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagerLoading,
        options: toSelectOptions(accountManagerOptions),
        getDisplayValue: values => {
          const option = accountManagerOptions.find(
            item => String(item.value) === String(values.accountManager)
          );
          return option ? option.text : undefined;
        }
      });
    }

    return fields;
  }, [
    isEstateUser,
    multiFranchiseOptions,
    accountManagerOptions,
    isAccountManagerLoading
  ]);

  const getData = useCallback(async () => {
    try {
      const filters = {
        page: `${currentPage}`,
        ...appliedFiltersRef.current
      };
      const franchises = await getFranchisesList(filters);
      if (currentPage === 1) setTotal(franchises.count);
      return franchises;
    } catch (e) {
      dangerNotification('Oops...', 'Não foi possível buscar os parceiros!');
      throw e;
    }
  }, [currentPage]);

  const { data, isFetching: isLoading } = useQuery(
    ['getFranchisesList', currentPage, appliedFilters],
    getData,
    {
      enabled: true,
      keepPreviousData: true,
      refetchOnMount: 'always'
    }
  );

  const handleFilterSearch = (values: FilterValues) => {
    setAppliedFilters(normalizeToApiFilter(values));
    setCurrentPage(1);
  };

  const handleFilterClear = () => {
    setCurrentPage(1);
  };

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <FilterBar
          fields={filterFields}
          initialValues={initialFilterValues}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="franchises"
        />
      </div>

      <FranchiseListPageTable
        franchises={data?.data || []}
        isLoading={isLoading}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        dataCount={total}
        handleSelectItem={(item: FranchiseState) => {
          setItemSelected(item);
          setIsViewModalOpen(true);
        }}
      />
      {itemSelected && (
        <ViewModal
          isOpen={isViewModalOpen}
          onClose={() => setIsViewModalOpen(false)}
          franchise={itemSelected}
        />
      )}
    </>
  );
};

export default connect(mapState, mapDispatch)(FranchiseListPage);
